// Drive.jsx
import React, { useEffect, useMemo, useState, useRef, useCallback } from 'react';

import api from '../api';
import { useTheme, alpha } from '@mui/material/styles';
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Fab,
  TextField,
  LinearProgress,
  Snackbar,
  Paper,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  InputAdornment,
  Badge,
  Stack,
  Grid,
  Menu,
  MenuItem,
  Divider,
  Chip,
} from '@mui/material';
import './styles.css';
import CreateNewFolderIcon from '@mui/icons-material/CreateNewFolder';
import UploadIcon from '@mui/icons-material/CloudUpload';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import StorageIcon from '@mui/icons-material/Storage';
import ShareIcon from '@mui/icons-material/Share';
import ShareOffIcon from '@mui/icons-material/Block';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import EditIcon from '@mui/icons-material/Edit';
import FolderIcon from '@mui/icons-material/Folder';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckIcon from '@mui/icons-material/Check';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DriveAppNav from '../Components/DriveNav.jsx'
const API_BASE = `${window.location.origin}/api/v1`;

export default function Drive() {
  const [selection, setSelection] = useState([]);
  const [view, setView] = useState('my');
  const [path, setPath] = useState('');
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('');
  const [uploading, setUp] = useState(false);
  const [toast, setToast] = useState('');
  const [sharedMine, setSharedMine] = useState(new Set());
  const [linkSharedMine, setLinkSharedMine] = useState(new Set());
  const [linkBusy, setLinkBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [unshareOpen, setUnshareOpen] = useState(false);
  const [peopleCsv, setPeopleCsv] = useState('');
  const [clipboard, setClipboard] = useState({ files: [], op: null }); // 'copy' | 'move'
  const [ctxMenu, setCtxMenu] = useState(null); // {mouseX, mouseY, file}
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [dropOver, setDropOver] = useState(false); // uploads area highlight
  const [dragInfo, setDragInfo] = useState(null); // {files}
  const [csrf, setCsrf] = useState('');
  const [ready, setReady] = useState(false);
  const [lastClickIndex, setLastClickIndex] = useState(null);
  const mainRef = useRef(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const folderBase = theme.palette.warning.main;
  const fileBase = theme.palette.info.main;
  const bgTint = (base) => alpha(base, isDark ? 0.1 : 0.06);
  const bgTintHover = (base) => alpha(base, isDark ? 0.16 : 0.1);

  const selectedFiles = useMemo(
    () => selection.filter((s) => !s.isDirectory),
    [selection]
  );

  const handleApiError = useCallback(
    (err, fallbackMessage = 'Something went wrong') => {
      console.error(err);

      let message = fallbackMessage;

      const data = err?.response?.data;

      if (data) {
        if (typeof data === 'string') {
          message = data;
        } else if (data.error) {
          message = data.error;
        } else if (data.message) {
          message = data.message;
        }
      } else if (err?.message && !fallbackMessage) {
        message = err.message;
      }

      setToast(message || 'Unexpected error');
    },
    []
  );



  useEffect(() => {
    (async () => {
      try {
        const r = await api.get('/csrf-token', { withCredentials: true });
        setCsrf(r.data.csrfToken);
      } finally {
        setReady(true);
      }
    })();
  }, []);

  const withSlash = (k) => (k.endsWith('/') ? k : k + '/');

  const toFM = (d) => {
    const isDir = d.type === 'folder';
    const relWithMaybeSlash = d.id.split('/').slice(1).join('/');
    const rel = relWithMaybeSlash.replace(/\/$/, '');
    return {
      id: isDir ? withSlash(d.id) : d.id,
      name: d.attributes?.name || rel.split('/').pop(),
      isDirectory: isDir,
      path: '/' + rel,
      size: d.attributes?.size,
      timestamp: d.attributes?.timestamp,
    };
  };

  const refreshMine = async (p) => {
    try {
      const r = await api.get('/drive/rfm', { params: { action: 'getfolder', path: p } });
      setItems(r.data.data.map(toFM));
    } catch (err) {
      handleApiError(err, 'Failed to load folder');
    }
  };

  const refreshShared = async () => {
    try {
      const r = await api.get('/drive/rfm', { params: { action: 'shared-with-me' } });
      setItems(r.data.data.map((k) => toFM({ id: k, type: 'file' })));
    } catch (err) {
      handleApiError(err, 'Failed to load shared items');
    }
  };

  useEffect(() => {
    if (view === 'my') {
      refreshMine(path);
      api
        .get('/drive/rfm', { params: { action: 'shared-by-me' } })
        .then((r) => setSharedMine(new Set(r.data.data)))
        .catch((err) =>
          handleApiError(err, 'Failed to load files shared by you')
        );
      api
        .get('/drive/rfm', { params: { action: 'link-shared-by-me' } })
        .then((r) => setLinkSharedMine(new Set(r.data.data)))
        .catch((err) => {
          setLinkSharedMine(new Set());
          handleApiError(err, 'Failed to load files shared via link');
        });
    } else {
      refreshShared();
    }
  }, [view, path]);

  // Paste upload (clipboard images/files)
  useEffect(() => {
    const onPaste = (e) => {
      const files = e.clipboardData?.files
        ? Array.from(e.clipboardData.files)
        : [...e.clipboardData.items]
          .filter((i) => i.kind === 'file')
          .map((i) => i.getAsFile())
          .filter(Boolean);
      if (!files.length) return;
      handlePasteUpload(files);
      e.preventDefault();
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [path]); // path included so uploads go into current folder

  const handlePasteUpload = async (files) => {
    const data = new FormData();
    files.forEach((f) => data.append('files[]', f));
    const query = path ? `?action=add&path=${encodeURIComponent(path)}` : '?action=add';
    setUp(true);
    api
      .post(`${API_BASE}/drive/rfm${query}`, data, {
        onUploadProgress: (ev) => setUp(!!(ev.total && ev.loaded < ev.total)),
      })
      .then(() => {
        setToast('Uploaded from clipboard');
        refreshMine(path);
      })
      .catch((err) => handleApiError(err, 'Paste upload failed'))
      .catch((err) => handleApiError(err, 'Upload failed'))
      .finally(() => setUp(false));
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setToast('Link copied to clipboard');
    } catch {
      // Fallback prompt so it works over http or without permissions
      window.prompt('Copy link', text);
      setToast('Link ready');
    }
  };

  const getOrCreateLink = async (file, autoCreate = true) => {
    if (!file || file.isDirectory) return null;
    setLinkBusy(true);
    try {
      // Try to get existing link
      const g = await api.get('/drive/rfm', { params: { action: 'get-link', path: file.id } });
      let url = g?.data?.data?.url || null;
      // Create if none
      if (!url && autoCreate) {
        const c = await api.post('/drive/rfm?action=create-link', { path: file.id });
        url = c?.data?.data?.url || null;
      }
      if (url) {
        copyToClipboard(url);
        return url;
      }
      setToast('No link available');
      return null;
    } catch (err) {
      handleApiError(err, 'Link operation failed');
      return null;
    } finally {
      setLinkBusy(false);
    }
  };

  const revokeLink = async (file) => {
    if (!file || file.isDirectory) return;
    setLinkBusy(true);
    try {
      await api.post('/drive/rfm?action=revoke-link', { path: file.id });
      setToast('Link revoked');
      if (view === 'my') refreshMine(path);
    } catch (err) {
      handleApiError(err, 'Failed to revoke link');
    } finally {
      setLinkBusy(false);
    }
  };

  const handleFMPaste = async (files, destinationFolder, operationType) => {
    const dest = destinationFolder ? destinationFolder.path.replace(/^\//, '') : path;
    const action = operationType === 'move' ? 'move' : 'copy';
    try {
      await api.post(`/drive/rfm?action=${action}`, {
        items: files.map((f) => f.id),
        destination: dest,
      });
      refreshMine(dest);
      setToast(`${action.charAt(0).toUpperCase() + action.slice(1)} succeeded`);
    } catch (err) {
      handleApiError(err, `${action.charAt(0).toUpperCase() + action.slice(1)} failed`);
    }
  };

  const goUp = () =>
    setPath((p) => p.split('/').slice(0, -1).join('/'));

  const createFolder = async () => {
    const name = window.prompt('Folder name');
    if (!name) return;
    try {
      await api.post('/drive/rfm?action=addfolder', { path, name });
      setToast('Folder created');
      refreshMine(path);
    } catch (err) {
      handleApiError(err, 'Failed to create folder');
    }
  };

  const handleDownload = async (files) => {
    const file = Array.isArray(files) ? files[0] : files;
    if (!file) return;
    try {
      const res = await api.get('/drive/rfm', {
        params: { action: 'download', path: file.id },
      });
      window.open(res.data.data.url, '_blank');
    } catch (err) {
      handleApiError(err, 'Failed to get download link');
    }
  };

  const handleRename = async (file, newName) => {
    if (!file) return;
    await api.post('/drive/rfm?action=rename', { item: file.isDirectory ? withSlash(file.id) : file.id, newName });
    try {
      await api.post('/drive/rfm?action=rename', {
        item: file.isDirectory ? withSlash(file.id) : file.id,
        newName,
      });
      setToast('Renamed');
      setEditingId(null);
      refreshMine(path);
    } catch (err) {
      handleApiError(err, 'Failed to rename');
    }
  };

  const doShare = async (emails) => {
    const users = emails
      .split(/[,\n]/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!users.length) return;
    try {
      await Promise.all(
        selectedFiles.map((f) =>
          api.post('/drive/rfm?action=share', {
            path: f.id,
            users,
            expiresIn: 86400,
          })
        )
      );
      setToast('Shared!');
      setShareOpen(false);
      setPeopleCsv('');
      refreshMine(path);
    } catch (err) {
      handleApiError(err, 'Failed to share file(s)');
    }
    setToast('Shared!');
    setShareOpen(false);
    setPeopleCsv('');
    refreshMine(path);
  };

  const doUnshare = async () => {
    try {
      await Promise.all(
        selectedFiles.map((f) =>
          api.post('/drive/rfm?action=unshare', { path: f.id })
        )
      );
      setToast('Un-shared');
      setUnshareOpen(false);
      refreshMine(path);
    } catch (err) {
      handleApiError(err, 'Failed to un-share file(s)');
    }
  };

  const visible = useMemo(() => {
    let filtered = filter
      ? items.filter((i) => i.name.toLowerCase().includes(filter.toLowerCase()))
      : items;

    return filtered.slice().sort((a, b) => {
      if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
      return a.name.localeCompare(b.name);
    });
  }, [items, filter]);


  // selection helpers
  const isSelected = (id) => selection.some((s) => s.id === id);
  const selectRange = (fromIdx, toIdx) => {
    const [start, end] = fromIdx < toIdx ? [fromIdx, toIdx] : [toIdx, fromIdx];
    const ranged = visible.slice(start, end + 1);
    setSelection(ranged);
  };
  const toggleSelect = (file, idx, e) => {
    if (e.shiftKey && lastClickIndex !== null) {
      selectRange(lastClickIndex, idx);
      return;
    }
    setLastClickIndex(idx);
    if (e.metaKey || e.ctrlKey) {
      setSelection((prev) =>
        isSelected(file.id) ? prev.filter((p) => p.id !== file.id) : [...prev, file]
      );
    } else {
      setSelection([file]);
    }
  };
  const selectAll = (checked) => setSelection(checked ? [...visible] : []);

  // clipboard actions
  const copySelected = () =>
    selection.length && setClipboard({ files: selection, op: 'copy' });
  const cutSelected = () =>
    selection.length && setClipboard({ files: selection, op: 'move' });
  const pasteClipboard = async (dest = null) => {
    if (!clipboard.files.length || !clipboard.op) return;
    await handleFMPaste(clipboard.files, dest || { path: `/${path}` }, clipboard.op);
    setClipboard({ files: [], op: null });
  };

  // list/grid open
  const openIfFolder = (file) => {
    if (file?.isDirectory) setPath(file.path.replace(/^\//, ''));
  };

  // context menu
  const onContextMenu = (e, file) => {
    e.preventDefault();
    setCtxMenu(
      ctxMenu === null
        ? { mouseX: e.clientX - 2, mouseY: e.clientY - 4, file }
        : null
    );
  };
  const closeMenu = () => setCtxMenu(null);

  // inline rename
  const startInlineRename = (file) => {
    setEditingId(file.id);
    setEditingName(file.name);
  };
  const commitInlineRename = (file) => {
    const trimmed = editingName.trim();
    if (!trimmed || trimmed === file.name) {
      setEditingId(null);
      return;
    }
    handleRename(file, trimmed);
  };

  // keyboard shortcuts (desktop-like)
  useEffect(() => {
    const onKey = (e) => {
      const meta = e.ctrlKey || e.metaKey;

      // Global paste into current folder
      if (meta && e.key.toLowerCase() === 'v') {
        e.preventDefault();
        pasteClipboard();
        return;
      }
      if (!selection.length && !meta) {
        if (e.key === 'Backspace') {
          e.preventDefault();
          goUp();
        }
        return;
      }

      if (meta && e.key.toLowerCase() === 'a') {
        // select all
        e.preventDefault();
        selectAll(true);
        return;
      }
      if (meta && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        copySelected();
        return;
      }
      if (meta && e.key.toLowerCase() === 'x') {
        e.preventDefault();
        cutSelected();
        return;
      }
      if (e.key === 'Delete') {
        e.preventDefault();
        deleteSelected();
        return;
      }
      if (e.key === 'F2' || e.key === 'f2') {
        e.preventDefault();
        startInlineRename(selection[0]);
        return;
      }
      if (e.key === 'Enter') {
        e.preventDefault();
        openIfFolder(selection[0]);
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        goUp();
        return;
      }
      if (e.key === 'Escape') {
        setSelection([]);
        setEditingId(null);
        closeMenu();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [selection, clipboard, path]); // deps kept the same

  // delete
  const deleteSelected = async () => {
    if (!selection.length) return;
    const ok = window.confirm(`Delete ${selection.length} item(s)?`);
    if (!ok) return;
    try {
      await api.post('/drive/rfm?action=delete', {
        items: selection.map((f) => f.id),
      });
      setToast('Deleted');
      setSelection([]);
      refreshMine(path);
    } catch (err) {
      handleApiError(err, 'Failed to delete items');
    }
  };

  // preview
  function FilePreview({ file }) {
    const [url, setUrl] = useState(null);

    useEffect(() => {
      if (!file || file.isDirectory) return;
      api
        .get('/drive/rfm', { params: { action: 'download', path: file.id } })
        .then((r) => setUrl(r.data.data.url))
        .catch(() => setUrl(null));
    }, [file]);

    if (!file || file.isDirectory) return null;
    if (!url) return <Box p={2}>Preview unavailable</Box>;

    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    const commonStyle = {
      maxWidth: '100%',
      maxHeight: '80vh',
      width: 'auto',
      height: 'auto',
      objectFit: 'contain',
      borderRadius: 12,
      boxShadow: '0 8px 26px rgba(0,0,0,.15)',
      background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
    };

    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) {
      return <img src={url} alt={file.name} style={commonStyle} />;
    }
    
    if (ext === 'pdf') {
      return (
        <iframe
          title={file.name}
          src={url}
          style={{
            border: 0,
            width: '100%',
            height: '85vh',
            borderRadius: 12,
            boxShadow: '0 8px 26px rgba(0,0,0,.15)',
            background: isDark
              ? 'rgba(255,255,255,0.02)'
              : 'rgba(0,0,0,0.02)',
          }}
        />
      );
    }
    return (
      <Box p={2}>
        <a href={url} target="_blank" rel="noreferrer">
          Open preview
        </a>
      </Box>
    );
  }

  // DnD helpers (move/copy between folders)
  const onRowDragStart = (e, file) => {
    const files = selection.length && isSelected(file.id) ? selection : [file];
    setDragInfo({ files });
    try {
      e.dataTransfer.setData(
        'text/plain',
        JSON.stringify(files.map((f) => f.id))
      );
      e.dataTransfer.effectAllowed = 'copyMove';
    } catch {
      // ignore
    }
  };
  const onRowDragEnd = () => setDragInfo(null);

  const onFolderDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = e.ctrlKey ? 'copy' : 'move';
  };
  const onFolderDrop = (e, folder) => {
    e.preventDefault();
    const op = e.ctrlKey ? 'copy' : 'move';
    const files = dragInfo?.files || [];
    if (!files.length || !folder?.isDirectory) return;
    handleFMPaste(files, folder, op);
  };

  // Upload by dropping files into the canvas
  useEffect(() => {
    const el = mainRef.current;
    if (!el) return;
    const onDragOver = (e) => {
      if (e.dataTransfer?.types?.includes('Files')) {
        e.preventDefault();
        setDropOver(true);
      }
    };
    const onDragLeave = (e) => {
      if (e.target === el) setDropOver(false);
    };
    const onDrop = (e) => {
      if (!e.dataTransfer?.files?.length) return;
      e.preventDefault();
      setDropOver(false);
      handlePasteUpload(Array.from(e.dataTransfer.files));
    };
    el.addEventListener('dragover', onDragOver);
    el.addEventListener('dragleave', onDragLeave);
    el.addEventListener('drop', onDrop);
    return () => {
      el.removeEventListener('dragover', onDragOver);
      el.removeEventListener('dragleave', onDragLeave);
      el.removeEventListener('drop', onDrop);
    };
  }, [path]); // path so drop targets current folder

  const formatBytes = (n) =>
    typeof n === 'number' ? Intl.NumberFormat().format(n) : '—';
  const formatDate = (t) =>
    t ? new Date(t).toLocaleString() : '—';

  return (
    <>
      {/* Top bar */}
      <AppBar
        position="sticky"
        color="inherit"
        elevation={0}
        sx={{
          backdropFilter: 'saturate(120%) blur(6px)',
          borderBottom: '1px solid',
          borderColor: isDark
            ? 'rgba(255,255,255,0.08)'
            : 'rgba(0,0,0,0.06)',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
            Drive
          </Typography>

          <Chip
            size="small"
            icon={<CheckIcon />}
            label="Tip: Hold Ctrl/Cmd while dropping to copy"
            sx={{ ml: 2, display: { xs: 'none', md: 'inline-flex' } }}
            variant="outlined"
          />

          <Box sx={{ flexGrow: 1 }} />

          <Tooltip title="My files">
            <IconButton
              color={view === 'my' ? 'primary' : 'default'}
              onClick={() => setView('my')}
              className="icon-soft"
            >
              <StorageIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Shared with me">
            <IconButton
              color={view === 'shared' ? 'primary' : 'default'}
              onClick={() => setView('shared')}
              className="icon-soft"
            >
              <ShareIcon />
            </IconButton>
          </Tooltip>

          <TextField
            size="small"
            placeholder="Search files…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: 160, sm: 240, md: 320 },
              '& .MuiOutlinedInput-root': {
                borderRadius: 10,
                backgroundColor: 'action.hover',
                '&:hover': { backgroundColor: 'action.selected' },
              },
            }}
          />
        </Toolbar>
        <DriveAppNav />
        {view === 'my' && (
          <Toolbar
            variant="dense"
            sx={{
              bgcolor: 'transparent',
              color: 'text.primary',
              borderTop: '1px solid',
              borderColor: isDark
                ? 'rgba(255,255,255,0.06)'
                : 'rgba(0,0,0,0.06)',
            }}
          >
            <Breadcrumbs separator="›">
              <Link
                color="inherit"
                onClick={() => {
                  setPath('');
                  refreshMine('');
                  setSelection([]);
                }}
                sx={{ cursor: 'pointer' }}
                onDragOver={(e) => onFolderDragOver(e)}
                onDrop={(e) =>
                  onFolderDrop(e, { isDirectory: true, path: '/' })
                }
              >
                Home
              </Link>
              {path
                .split('/')
                .filter(Boolean)
                .map((c, i, arr) => {
                  const crumbPath = arr.slice(0, i + 1).join('/');
                  return (
                    <Link
                      key={i}
                      color="inherit"
                      onClick={() => {
                        setPath(crumbPath);
                        setSelection([]);
                      }}
                      sx={{ cursor: 'pointer' }}
                      onDragOver={(e) => onFolderDragOver(e)}
                      onDrop={(e) =>
                        onFolderDrop(e, {
                          isDirectory: true,
                          path: `/${crumbPath}`,
                        })
                      }
                    >
                      {c}
                    </Link>
                  );
                })}
            </Breadcrumbs>

            <Stack direction="row" spacing={1} sx={{ ml: 'auto' }}>
              <Tooltip title="Refresh">
                <IconButton
                  onClick={() =>
                    view === 'my' ? refreshMine(path) : refreshShared()
                  }
                  className="icon-soft"
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>
              <IconButton
                onClick={goUp}
                disabled={!path}
                className="icon-soft"
              >
                <ArrowUpwardIcon />
              </IconButton>
            </Stack>
          </Toolbar>
        )}
      </AppBar>

      {uploading && (
        <LinearProgress
          sx={{
            height: 3,
            borderRadius: 8,
            '.MuiLinearProgress-bar': { borderRadius: 8 },
          }}
        />
      )}

      {/* Canvas */}
      <Box
        ref={mainRef}
        sx={{
          position: 'relative',
          flex: 1,
          height: 'calc(100vh - 128px)',
          p: { xs: 1, sm: 2 },
          background: isDark
            ? 'linear-gradient(180deg, rgba(255,255,255,0.02), transparent 200px)'
            : 'linear-gradient(180deg, rgba(0,0,0,0.03), transparent 200px)',
        }}
      >
        {/* Drop overlay for uploads */}
        {dropOver && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              border: '2px dashed',
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
              display: 'grid',
              placeItems: 'center',
              zIndex: 5,
              pointerEvents: 'none',
            }}
          >
            <Stack alignItems="center" spacing={1}>
              <UploadIcon />
              <Typography variant="subtitle1">
                Drop files to upload into /{path}
              </Typography>
            </Stack>
          </Box>
        )}

        {/* Selection action bar */}
        {view === 'my' && selection.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              position: 'fixed',
              bottom: 24,
              left: 24,
              display: 'flex',
              gap: 1,
              alignItems: 'center',
              p: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: isDark
                ? 'rgba(255,255,255,0.1)'
                : 'rgba(0,0,0,0.08)',
              boxShadow: '0 8px 26px rgba(0,0,0,.12)',
              backgroundColor: 'background.paper',
              zIndex: 1300,
            }}
          >
            {selectedFiles.length &&
              selectedFiles.every((f) => sharedMine.has(f.id)) ? (
              <Tooltip title="Stop sharing">
                <IconButton
                  color="error"
                  onClick={() => setUnshareOpen(true)}
                  className="icon-soft"
                >
                  <ShareOffIcon />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip title="Share">
                <IconButton
                  color="primary"
                  onClick={() => setShareOpen(true)}
                  className="icon-soft"
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            )}

            <Tooltip title="Copy (Ctrl/Cmd+C)">
              <IconButton onClick={copySelected} className="icon-soft">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Cut (Ctrl/Cmd+X)">
              <IconButton onClick={cutSelected} className="icon-soft">
                <ContentCutIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={`Paste into /${path || ''} (Ctrl/Cmd+V)`}>
              <span>
                <IconButton
                  onClick={() => pasteClipboard()}
                  className="icon-soft"
                  disabled={!clipboard.files.length || !clipboard.op}
                >
                  <DriveFileMoveIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Rename (F2)">
              <span>
                <IconButton
                  onClick={() => selection[0] && startInlineRename(selection[0])}
                  disabled={selection.length !== 1}
                  className="icon-soft"
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Download">
              <span>
                <IconButton
                  onClick={() => handleDownload(selection[0])}
                  disabled={
                    !selection.length || selection[0]?.isDirectory
                  }
                  className="icon-soft"
                >
                  <DownloadIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Tooltip title="Get link (creates if missing)">
              <span>
                <IconButton
                  onClick={() => getOrCreateLink(selection[0])}
                  disabled={
                    selection.length !== 1 ||
                    selection[0]?.isDirectory ||
                    linkBusy
                  }
                  className="icon-soft"
                >
                  <LinkIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Revoke link">
              <span>
                <IconButton
                  onClick={() => revokeLink(selection[0])}
                  disabled={
                    selection.length !== 1 ||
                    selection[0]?.isDirectory ||
                    linkBusy
                  }
                  className="icon-soft"
                  color="error"
                >
                  <LinkOffIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Delete (Del)">
              <IconButton
                color="error"
                onClick={deleteSelected}
                className="icon-soft"
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Paper>
        )}

        {/* File area */}
        {!ready || !csrf ? (
          <LinearProgress
            sx={{
              height: 3,
              borderRadius: 8,
              '.MuiLinearProgress-bar': { borderRadius: 8 },
            }}
          />
        ) : (
          <Box
            className="rfm-skin"
            sx={{
              height: '100%',
              borderRadius: 12,
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              backgroundColor: 'background.paper',
              boxShadow: '0 6px 20px rgba(0,0,0,.06)',
              display: 'grid',
              gridTemplateColumns: { md: '2fr 1fr', xs: '1fr' },
            }}
            onContextMenu={(e) => {
              // empty-space context menu
              if (e.target === e.currentTarget) onContextMenu(e, null);
            }}
          >
            {/* List/Grid */}
            <Box sx={{ overflow: 'auto', p: 2 }}>
              <Grid container spacing={2}>
                {visible.map((f, idx) => (
                  <Grid item xs={6} sm={4} md={3} lg={2.4} key={f.id}>
                    <Paper
                      variant={isSelected(f.id) ? 'elevation' : 'outlined'}
                      elevation={isSelected(f.id) ? 4 : 0}
                      sx={{
                        p: 1.2,
                        borderRadius: 2,
                        borderWidth: 1,
                        borderStyle: 'solid',
                        borderColor: isSelected(f.id)
                          ? f.isDirectory
                            ? folderBase
                            : fileBase
                          : 'divider',
                        backgroundColor: isSelected(f.id)
                          ? alpha(
                            f.isDirectory ? folderBase : fileBase,
                            isDark ? 0.18 : 0.14
                          )
                          : bgTint(
                            f.isDirectory ? folderBase : fileBase
                          ),
                        cursor: f.isDirectory ? 'pointer' : 'default',
                        '&:hover': {
                          borderColor: f.isDirectory
                            ? folderBase
                            : fileBase,
                          backgroundColor: bgTintHover(
                            f.isDirectory ? folderBase : fileBase
                          ),
                        },
                        transition:
                          'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
                        userSelect: 'none',
                      }}
                      draggable
                      onDragStart={(e) => onRowDragStart(e, f)}
                      onDragEnd={onRowDragEnd}
                      onDragOver={(e) =>
                        f.isDirectory && onFolderDragOver(e)
                      }
                      onDrop={(e) =>
                        f.isDirectory && onFolderDrop(e, f)
                      }
                      onDoubleClick={() => openIfFolder(f)}
                      onClick={(e) => toggleSelect(f, idx, e)}
                      onContextMenu={(e) => onContextMenu(e, f)}
                    >
                      <Stack alignItems="center" spacing={1}>
                        <Badge
                          overlap="circular"
                          color="secondary"
                          badgeContent={
                            view === 'shared' && !f.isDirectory ? (
                              <ShareIcon fontSize="inherit" />
                            ) : view === 'my' &&
                              sharedMine.has(f.id) ? (
                              <ShareIcon fontSize="inherit" />
                            ) : view === 'my' &&
                              linkSharedMine.has(f.id) ? (
                              <LinkIcon fontSize="inherit" />
                            ) : null
                          }
                        >
                          {f.isDirectory ? (
                            <FolderIcon
                              fontSize="large"
                              sx={{ color: folderBase }}
                            />
                          ) : (
                            <InsertDriveFileIcon
                              fontSize="large"
                              sx={{ color: fileBase }}
                            />
                          )}
                        </Badge>
                        {editingId === f.id ? (
                          <TextField
                            size="small"
                            value={editingName}
                            autoFocus
                            onChange={(e) =>
                              setEditingName(e.target.value)
                            }
                            onBlur={() => commitInlineRename(f)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter')
                                commitInlineRename(f);
                              if (e.key === 'Escape')
                                setEditingId(null);
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            align="center"
                            sx={{
                              width: '100%',
                              whiteSpace: 'nowrap',
                              textOverflow: 'ellipsis',
                              overflow: 'hidden',
                            }}
                          >
                            {f.name}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="text.secondary"
                        >
                          {f.isDirectory
                            ? 'Folder'
                            : `${formatBytes(
                              f.size
                            )} • ${formatDate(f.timestamp)}`}
                        </Typography>
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Preview pane */}
            <Box
              sx={{
                display: { xs: 'none', md: 'block' },
                borderLeft: '1px solid',
                borderColor: 'divider',
                p: 2,
                overflow: 'auto',
              }}
            >
              <FilePreview file={selectedFiles[0]} />
            </Box>
          </Box>
        )}

        {/* Context menu */}
        <Menu
          open={!!ctxMenu}
          onClose={closeMenu}
          anchorReference="anchorPosition"
          anchorPosition={
            ctxMenu
              ? { top: ctxMenu.mouseY, left: ctxMenu.mouseX }
              : undefined
          }
        >
          {ctxMenu?.file ? (
            <>
              <MenuItem
                onClick={() => {
                  openIfFolder(ctxMenu.file);
                  closeMenu();
                }}
              >
                Open
              </MenuItem>
              <MenuItem
                onClick={() => {
                  startInlineRename(ctxMenu.file);
                  closeMenu();
                }}
              >
                Rename
              </MenuItem>
              {!ctxMenu.file.isDirectory && (
                <MenuItem
                  onClick={() => {
                    handleDownload(ctxMenu.file);
                    closeMenu();
                  }}
                >
                  Download
                </MenuItem>
              )}
              {!ctxMenu.file.isDirectory && (
                <MenuItem
                  onClick={async () => {
                    await getOrCreateLink(ctxMenu.file);
                    closeMenu();
                  }}
                >
                  Get link
                </MenuItem>
              )}
              {!ctxMenu.file.isDirectory && (
                <MenuItem
                  onClick={async () => {
                    await revokeLink(ctxMenu.file);
                    closeMenu();
                  }}
                >
                  Revoke link
                </MenuItem>
              )}
              <Divider />
              <MenuItem
                onClick={() => {
                  setShareOpen(true);
                  closeMenu();
                }}
              >
                Share
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setUnshareOpen(true);
                  closeMenu();
                }}
              >
                Un-share
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setSelection([ctxMenu.file]);
                  copySelected();
                  closeMenu();
                }}
              >
                Copy
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSelection([ctxMenu.file]);
                  cutSelected();
                  closeMenu();
                }}
              >
                Cut
              </MenuItem>
              <MenuItem
                disabled={!clipboard.files.length || !clipboard.op}
                onClick={() => {
                  pasteClipboard(
                    ctxMenu.file.isDirectory ? ctxMenu.file : null
                  );
                  closeMenu();
                }}
              >
                Paste{' '}
                {ctxMenu.file?.isDirectory
                  ? `into "${ctxMenu.file.name}"`
                  : ''}
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  setSelection([ctxMenu.file]);
                  deleteSelected();
                  closeMenu();
                }}
                sx={{ color: 'error.main' }}
              >
                Delete
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem
                onClick={() => {
                  createFolder();
                  closeMenu();
                }}
              >
                New folder…
              </MenuItem>
              <MenuItem
                disabled={!clipboard.files.length || !clipboard.op}
                onClick={() => {
                  pasteClipboard();
                  closeMenu();
                }}
              >
                Paste here
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  view === 'my' ? refreshMine(path) : refreshShared();
                  closeMenu();
                }}
              >
                Refresh
              </MenuItem>
            </>
          )}
        </Menu>

        {/* Dialogs */}
        <Dialog
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 3,
              border: '1px solid',
              borderColor: isDark
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.06)',
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Share {selectedFiles.length} item
            {selectedFiles.length > 1 ? 's' : ''}
          </DialogTitle>
          <DialogContent sx={{ minWidth: 320 }}>
            <DialogContentText sx={{ mb: 1 }}>
              Enter e-mail addresses or usernames, separated by commas or
              new lines.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              label="People"
              multiline
              fullWidth
              value={peopleCsv}
              onChange={(e) => setPeopleCsv(e.target.value)}
            />
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setShareOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => doShare(peopleCsv)}>
              Share
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={unshareOpen}
          onClose={() => setUnshareOpen(false)}
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 3,
              border: '1px solid',
              borderColor: isDark
                ? 'rgba(255,255,255,0.08)'
                : 'rgba(0,0,0,0.06)',
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700 }}>
            Stop sharing?
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              This will remove access for everyone on the selected{' '}
              {selectedFiles.length} file
              {selectedFiles.length > 1 ? 's' : ''}.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setUnshareOpen(false)}>Cancel</Button>
            <Button color="error" variant="contained" onClick={doUnshare}>
              Un-share
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {view === 'my' && (
        <>
          <Fab
            color="secondary"
            className="fab-lift"
            sx={{ position: 'fixed', bottom: 24, right: 24, boxShadow: 6 }}
            component="label"
          >
            <UploadIcon />
            <input
  hidden
  multiple
  type="file"
  onChange={(e) => {
    const data = new FormData();
    [...e.target.files].forEach((f) => data.append('files[]', f));

    const query = path
      ? `?action=add&path=${encodeURIComponent(path)}`
      : '?action=add';

    setUp(true);

    api
      .post(`/drive/rfm${query}`, data, {
        onUploadProgress: (ev) => setUp(!!(ev.total && ev.loaded < ev.total)),
      })
      .then(() => {
        setToast('Uploaded');
        refreshMine(path);
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          'Upload failed';

        setToast(msg);
      })
      .finally(() => {
        setUp(false);
        e.target.value = '';
      });
  }}
/>

          </Fab>

          <Fab
            color="primary"
            className="fab-lift"
            sx={{
              position: 'fixed',
              bottom: 94,
              right: 24,
              boxShadow: 8,
            }}
            onClick={createFolder}
          >
            <CreateNewFolderIcon />
          </Fab>
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        ContentProps={{
          sx: { borderRadius: 2, boxShadow: 3 },
        }}
      />
    </>
  );
}
